import React, { useState, useEffect, useMemo } from 'react';
import { ipfsGateway, resolveIpfsUrl, BLEND_CONTRACT, tokenContracts, tokenDecimals } from '../constants/gameConfig';
import placeholderImage from '../assets/equip.png';

function BlendingPage({ session, nfts = [], playerData, tokenBalances, areBalancesLoaded, refreshNfts }) {
    // 1. Component State
    const [blends, setBlends] = useState([]);
    const [isLoadingBlends, setIsLoadingBlends] = useState(true);
    const [isBlending, setIsBlending] = useState(false);
    const [feedback, setFeedback] = useState('');
    const [selectedBlend, setSelectedBlend] = useState(null);
    const [selectedAssets, setSelectedAssets] = useState({});
    const [schemaFilter, setSchemaFilter] = useState('all');
    const [isRefreshing, setIsRefreshing] = useState(false);

    // 2. Fetch On-Chain Recipes from whatifblends & Enrich with AtomicAssets Data
    useEffect(() => {
        let isMounted = true;

        const fetchBlends = async () => {
            setIsLoadingBlends(true);
            const WAX_RPC_ENDPOINTS = [
                "https://wax.greymass.com",
                "https://api.wax.alohaeos.com",
                "https://wax.eosphere.io",
                "https://wax.eosdac.io",
                "https://wax.eu.eosamsterdam.net"
            ];

            let blendsFromDb = [];
            let fetchSuccess = false;

            for (const endpoint of WAX_RPC_ENDPOINTS) {
                try {
                    const controller = new AbortController();
                    const timeoutId = setTimeout(() => controller.abort(), 4000);

                    const response = await fetch(`${endpoint}/v1/chain/get_table_rows`, {
                        method: "POST",
                        headers: { "Content-Type": "text/plain" },
                        body: JSON.stringify({
                            json: true,
                            code: BLEND_CONTRACT,
                            scope: BLEND_CONTRACT,
                            table: "recipes",
                            limit: 500
                        }),
                        signal: controller.signal
                    });
                    clearTimeout(timeoutId);

                    if (response.ok) {
                        const data = await response.json();
                        blendsFromDb = (data.rows || [])
                            .filter(row => row.is_active === 1 || row.is_active === true)
                            .map(row => {
                                const ingredients = [];

                                // Parse NFT inputs
                                (row.nft_inputs || []).forEach(nft => {
                                    ingredients.push({
                                        type: 'NFT',
                                        templateId: Number(nft.template_id),
                                        count: Number(nft.count),
                                        collectionName: row.burn_collection
                                    });
                                });

                                // Parse Token inputs with space separator (" ")
                                (row.token_inputs || []).forEach(token => {
                                    const parts = (token.quantity || "").trim().split(" ");
                                    ingredients.push({
                                        type: 'TOKEN',
                                        symbol: parts[1] || "",
                                        amount: parseFloat(parts[0]) || 0,
                                        contract: token.token_contract
                                    });
                                });

                                return {
                                    id: row.id,
                                    blendName: row.blend_name,
                                    resultTemplateId: Number(row.result_template_id),
                                    resultCollectionName: row.result_collection,
                                    resultSchemaName: row.result_schema,
                                    burnCollectionName: row.burn_collection,
                                    blendLimit: Number(row.blend_limit || 0),
                                    ingredients: ingredients
                                };
                            });

                        fetchSuccess = true;
                        break;
                    }
                } catch (err) {
                    console.warn(`Node ${endpoint} failed, rotating to next endpoint...`);
                }
            }

            if (!fetchSuccess) {
                if (isMounted) {
                    setFeedback("Failed to fetch recipes from chain nodes. Please try refreshing.");
                    setIsLoadingBlends(false);
                }
                return;
            }

            // Collect all template IDs to batch fetch template images/data
            const allTemplateIds = new Set();
            blendsFromDb.forEach(blend => {
                if (blend.resultTemplateId) allTemplateIds.add(blend.resultTemplateId);
                blend.ingredients.forEach(ing => {
                    if (ing.type === 'NFT' && ing.templateId) allTemplateIds.add(ing.templateId);
                });
            });

            const templateDataMap = new Map();
            const atomicApiEndpoints = [
                "https://wax-atomic-api.eosphere.io",
                "https://wax.api.atomicassets.io",
                "https://atomic.wax.eosrio.io",
                "https://aa.neftyblocks.com",
                "https://aa.dapplica.io"
            ];

            const templatePromises = Array.from(allTemplateIds).map(async (templateId) => {
                if (!templateId) return;

                const blendUsingTemplate = blendsFromDb.find(b =>
                    b.resultTemplateId === templateId ||
                    b.ingredients.some(i => i.type === 'NFT' && i.templateId === templateId)
                );

                let collectionName = 'whatifreward';
                if (blendUsingTemplate) {
                    if (blendUsingTemplate.resultTemplateId === templateId && blendUsingTemplate.resultCollectionName) {
                        collectionName = blendUsingTemplate.resultCollectionName;
                    } else {
                        const ingredient = blendUsingTemplate.ingredients.find(i => i.type === 'NFT' && i.templateId === templateId);
                        collectionName = ingredient?.collectionName || blendUsingTemplate.burnCollectionName || 'whatifreward';
                    }
                }

                for (const endpoint of atomicApiEndpoints) {
                    try {
                        const response = await fetch(`${endpoint}/atomicassets/v1/templates/${collectionName}/${templateId}`, {
                            signal: AbortSignal.timeout(4000)
                        });
                        if (!response.ok) continue;
                        const data = await response.json();
                        if (data.success && data.data) {
                            templateDataMap.set(templateId, data.data);
                            return;
                        }
                    } catch (e) {
                        // rotate to next endpoint
                    }
                }

                // Fallback placeholder if metadata fails
                templateDataMap.set(templateId, {
                    name: `Template #${templateId}`,
                    collection: { collection_name: collectionName },
                    immutable_data: { img: null }
                });
            });

            await Promise.all(templatePromises);

            if (isMounted) {
                const enrichedBlends = blendsFromDb.map(blend => ({
                    ...blend,
                    resultTemplateData: templateDataMap.get(blend.resultTemplateId) || { name: `Template #${blend.resultTemplateId}`, immutable_data: { img: null } },
                    ingredients: blend.ingredients.map(ing => (
                        ing.type === 'NFT'
                            ? { ...ing, templateData: templateDataMap.get(ing.templateId) || { name: `Template #${ing.templateId}`, immutable_data: { img: null } } }
                            : ing
                    ))
                }));

                setBlends(enrichedBlends);
                setIsLoadingBlends(false);
            }
        };

        fetchBlends();
        return () => { isMounted = false; };
    }, []);

    // 3. User NFT Inventory Counter (Type-Safe Number Parsing)
    const userNftCountsByTemplate = useMemo(() => {
        const counts = new Map();
        (nfts || []).forEach(nft => {
            const templateId = Number(nft.template?.template_id);
            if (templateId) {
                counts.set(templateId, (counts.get(templateId) || 0) + 1);
            }
        });
        return counts;
    }, [nfts]);

    // 4. Token Balance Verification
    const requiredTokensForVisibleBlends = useMemo(() => {
        const required = new Set();
        blends
            .filter(blend => schemaFilter === 'all' || blend.resultSchemaName === schemaFilter)
            .forEach(blend => {
                blend.ingredients.forEach(ing => {
                    if (ing.type === 'TOKEN' && ing.symbol) {
                        required.add(ing.symbol);
                    }
                });
            });
        return required;
    }, [blends, schemaFilter]);

    const areAllRequiredBalancesLoaded = useMemo(() => {
        if (!areBalancesLoaded || !tokenBalances) return false;
        for (const token of requiredTokensForVisibleBlends) {
            if (!tokenBalances[token]) return false;
        }
        return true;
    }, [tokenBalances, requiredTokensForVisibleBlends, areBalancesLoaded]);

    // 5. Select Asset for Burning
    const handleSelectAsset = (templateId, assetId) => {
        const numericTpl = Number(templateId);
        setSelectedAssets(prev => {
            const currentSelection = prev[numericTpl] || [];
            const isSelected = currentSelection.includes(assetId);
            const requiredCount = selectedBlend?.ingredients.find(
                ing => ing.type === 'NFT' && Number(ing.templateId) === numericTpl
            )?.count || 0;

            let newSelection;
            if (isSelected) {
                newSelection = currentSelection.filter(id => id !== assetId);
            } else {
                if (currentSelection.length < requiredCount) {
                    newSelection = [...currentSelection, assetId];
                } else {
                    alert(`You can only select ${requiredCount} of this item.`);
                    newSelection = currentSelection;
                }
            }
            return { ...prev, [numericTpl]: newSelection };
        });
    };

    // 6. Execute On-Chain Multi-Action Blend
    const handlePerformBlend = async () => {
        if (!selectedBlend || !session || !playerData || playerData?.isBlacklisted || isBlending) {
            setFeedback("Cannot perform blend. Check balances or wallet connection.");
            return;
        }

        const nftIngredients = selectedBlend.ingredients.filter(i => i.type === 'NFT');
        const tokenIngredients = selectedBlend.ingredients.filter(i => i.type === 'TOKEN');
        const allAssetIdsToBurn = Object.values(selectedAssets).flat();

        const isReadyToBlendNFTs = nftIngredients.every(
            ing => (selectedAssets[Number(ing.templateId)]?.length || 0) === ing.count
        );

        if (!isReadyToBlendNFTs) {
            setFeedback("Error: Please select all required NFTs first.");
            return;
        }

        const totalRequiredNftCount = nftIngredients.reduce((sum, ing) => sum + ing.count, 0);
        if (totalRequiredNftCount !== allAssetIdsToBurn.length) {
            setFeedback("Error: Selected NFT count does not match the recipe requirements.");
            return;
        }

        // Token Balance Check
        for (const token of tokenIngredients) {
            const balanceString = tokenBalances[token.symbol];
            const balanceNum = parseFloat((balanceString || "").split(" ")[0]) || 0;
            if (balanceNum < token.amount) {
                setFeedback(`Error: Insufficient ${token.symbol} balance. Required: ${token.amount}, You have: ${balanceNum}`);
                return;
            }
        }

        if (!window.confirm(`Confirm permanent burn of ingredients for "${selectedBlend.blendName}"? This cannot be undone.`)) {
            return;
        }

        setIsBlending(true);
        try {
            setFeedback("Step 1 of 3: Please sign the atomic transaction in your wallet...");
            const actions = [];
            const memo = `BLEND_RECIPE:${selectedBlend.id}`;

            // 1. AtomicAssets Transfer Action
            if (allAssetIdsToBurn.length > 0) {
                actions.push({
                    account: 'atomicassets',
                    name: 'transfer',
                    authorization: [session.permissionLevel],
                    data: {
                        from: session.actor.toString(),
                        to: BLEND_CONTRACT,
                        asset_ids: allAssetIdsToBurn,
                        memo: memo
                    }
                });
            }

            // 2. Token Transfer Action(s)
            for (const token of tokenIngredients) {
                const contract = tokenContracts[token.symbol] || 'yoyinsoly214';
                const decimals = tokenDecimals[token.symbol] !== undefined ? tokenDecimals[token.symbol] : 8;
                const quantity = `${Number(token.amount).toFixed(decimals)} ${token.symbol}`;

                actions.push({
                    account: contract,
                    name: 'transfer',
                    authorization: [session.permissionLevel],
                    data: {
                        from: session.actor.toString(),
                        to: BLEND_CONTRACT,
                        quantity: quantity,
                        memo: memo
                    }
                });
            }

            if (actions.length === 0) throw new Error("No ingredients found for this blend.");

            const txResult = await session.transact({ actions }, { blocksBehind: 3, expireSeconds: 90 });
            const txId = txResult.response?.transaction_id || txResult.transaction_id;
            if (!txId) throw new Error("Transaction broadcast failed.");

            setFeedback("Step 2 of 3: Transfer confirmed on-chain. Finalizing item mint...");
            await new Promise(resolve => setTimeout(resolve, 3500));

            setFeedback("Step 3 of 3: Refreshing inventory...");
            setSelectedBlend(null);
            setSelectedAssets({});

            if (refreshNfts) await refreshNfts();
            setFeedback("🎉 Blend successful! Your new item has been crafted.");
            setTimeout(() => setFeedback(''), 6000);
        } catch (error) {
            console.error("Blend Execution Error:", error);
            setFeedback(`Error: ${error.message}`);
        } finally {
            setIsBlending(false);
        }
    };

    const handleRefresh = async () => {
        if (isRefreshing) return;
        setIsRefreshing(true);
        setFeedback('Refreshing your NFT inventory...');
        try {
            if (refreshNfts) await refreshNfts();
            setFeedback('Inventory refreshed successfully!');
        } catch (e) {
            setFeedback(`Error refreshing inventory: ${e.message}`);
        } finally {
            setIsRefreshing(false);
        }
    };

    const filteredBlends = useMemo(() => {
        if (schemaFilter === 'all') return blends;
        return blends.filter(blend => blend.resultSchemaName === schemaFilter);
    }, [blends, schemaFilter]);

    const resultSchemas = ['all', 'fireman', 'gear', 'deeds', 'emblems', 'gangpass', 'packs'];

    // 7. Ingredients Selection Modal Component
    const SelectionModal = () => {
        if (!selectedBlend) return null;

        const nftIngredients = selectedBlend.ingredients.filter(i => i.type === 'NFT');
        const tokenIngredients = selectedBlend.ingredients.filter(i => i.type === 'TOKEN');

        const isNftSelectionComplete = nftIngredients.every(
            ing => (selectedAssets[Number(ing.templateId)]?.length || 0) === ing.count
        );

        const hasEnoughTokens = tokenIngredients.every(ing => {
            const balanceString = tokenBalances[ing.symbol];
            const balanceNum = parseFloat((balanceString || "").split(" ")[0]) || 0;
            return balanceNum >= ing.amount;
        });

        const isReadyToBlend = isNftSelectionComplete && hasEnoughTokens;

        return (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
                <div style={{ backgroundColor: '#1e1e1e', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto', border: '1px solid #555' }}>
                    <h3 style={{ marginTop: 0, textAlign: 'center', color: '#ff9800' }}>Select Ingredients for: {selectedBlend.blendName}</h3>

                    {tokenIngredients.length > 0 && (
                        <div style={{ marginBottom: '20px', borderBottom: '1px solid #333', paddingBottom: '12px' }}>
                            <h4 style={{ color: '#00e5ff', margin: '0 0 8px 0' }}>Required Tokens:</h4>
                            {tokenIngredients.map((ing, idx) => {
                                const balanceString = tokenBalances[ing.symbol];
                                const balanceNum = parseFloat((balanceString || "").split(" ")[0]) || 0;
                                const hasEnough = balanceNum >= ing.amount;
                                return (
                                    <p key={idx} style={{ margin: '4px 0', color: hasEnough ? '#4caf50' : '#ff4b2b', fontSize: '14px' }}>
                                        • {ing.amount.toLocaleString()} {ing.symbol} (Your Balance: {balanceNum.toLocaleString()} {ing.symbol})
                                    </p>
                                );
                            })}
                        </div>
                    )}

                    {nftIngredients.map(ing => {
                        const numericTpl = Number(ing.templateId);
                        const availableNfts = (nfts || []).filter(nft => Number(nft.template?.template_id) === numericTpl);
                        const selectedCount = selectedAssets[numericTpl]?.length || 0;

                        return (
                            <div key={numericTpl} style={{ marginBottom: '20px' }}>
                                <p style={{ margin: '0 0 8px 0', fontWeight: 'bold' }}>
                                    Select {ing.count}x {ing.templateData?.name || `Template #${numericTpl}`}
                                </p>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '10px', border: '1px solid #333', padding: '10px', maxHeight: '200px', overflowY: 'auto', borderRadius: '6px', background: 'rgba(0,0,0,0.3)' }}>
                                    {availableNfts.length > 0 ? availableNfts.map(nft => {
                                        const isSelected = (selectedAssets[numericTpl] || []).includes(nft.asset_id);
                                        const rawImg = nft.data?.img || nft.template?.immutable_data?.img;
                                        const imgSrc = resolveIpfsUrl(rawImg) || placeholderImage;

                                        return (
                                            <div
                                                key={nft.asset_id}
                                                onClick={() => handleSelectAsset(numericTpl, nft.asset_id)}
                                                style={{
                                                    border: `2px solid ${isSelected ? '#00e5ff' : '#444'}`,
                                                    padding: '6px',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    textAlign: 'center',
                                                    background: isSelected ? 'rgba(0, 229, 255, 0.15)' : 'rgba(255,255,255,0.03)'
                                                }}
                                            >
                                                <img src={imgSrc} alt={nft.name || 'NFT'} style={{ width: '70px', height: '70px', objectFit: 'contain' }} />
                                                <p style={{ fontSize: '11px', margin: '4px 0 0 0', color: '#ccc' }}>#{String(nft.asset_id).slice(-6)}</p>
                                            </div>
                                        );
                                    }) : (
                                        <p style={{ color: '#888', fontSize: '13px', margin: '10px' }}>You do not hold any NFTs of this template.</p>
                                    )}
                                </div>
                                <p style={{ color: selectedCount === ing.count ? '#4caf50' : '#ff9800', textAlign: 'right', marginTop: '6px', fontSize: '13px', fontWeight: 'bold' }}>
                                    Selected: {selectedCount} / {ing.count}
                                </p>
                            </div>
                        );
                    })}

                    <div style={{ display: 'flex', gap: '10px', marginTop: '25px' }}>
                        <button onClick={() => { setSelectedBlend(null); setSelectedAssets({}); }} className="modern-button" style={{ background: '#555', flex: 1 }}>
                            Cancel
                        </button>
                        <button
                            onClick={handlePerformBlend}
                            disabled={!isReadyToBlend || isBlending}
                            className="modern-button"
                            style={{
                                background: isReadyToBlend ? 'linear-gradient(145deg, #28a745, #218838)' : '#333',
                                cursor: isReadyToBlend ? 'pointer' : 'not-allowed',
                                flex: 2
                            }}
                        >
                            {isBlending ? 'Processing...' : 'Perform Blend'}
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div style={{ backgroundColor: 'rgba(0,0,0,0.75)', padding: '25px', borderRadius: '12px', maxWidth: '1200px', margin: 'auto', border: '1px solid #333' }}>
            <SelectionModal />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                <h2 style={{ color: '#ff9800', margin: 0 }}>The Alchemist's Forge</h2>
                <button onClick={handleRefresh} disabled={isRefreshing || isBlending} className="modern-button" style={{ margin: 0, padding: '8px 16px' }}>
                    {isRefreshing ? 'Refreshing...' : 'Refresh Inventory'}
                </button>
            </div>

            <p style={{ color: '#ccc', margin: '0 0 20px 0' }}>
                Combine your ecosystem items and game tokens to forge powerful upgrades. Burned ingredients are permanently destroyed on-chain.
            </p>

            {feedback && (
                <p style={{ color: feedback.includes('Error') || feedback.includes('Failed') ? '#ff4b2b' : '#4caf50', backgroundColor: '#222', padding: '12px', borderRadius: '6px', margin: '15px 0' }}>
                    {feedback}
                </p>
            )}

            {/* Schema Filter Bar */}
            <div style={{ margin: '20px 0', textAlign: 'center' }}>
                <span style={{ marginRight: '10px', color: '#aaa', fontSize: '14px' }}>Filter by Schema:</span>
                {resultSchemas.map(schema => (
                    <button
                        key={schema}
                        onClick={() => setSchemaFilter(schema)}
                        className="modern-button"
                        style={{
                            margin: '0 4px',
                            padding: '6px 12px',
                            backgroundColor: schemaFilter === schema ? '#ff4b2b' : '#333',
                            textTransform: 'capitalize'
                        }}
                    >
                        {schema === 'all' ? 'All Recipes' : schema}
                    </button>
                ))}
            </div>

            {isLoadingBlends ? (
                <p style={{ textAlign: 'center', color: '#00e5ff', padding: '40px' }}>Loading on-chain forge recipes...</p>
            ) : !areAllRequiredBalancesLoaded ? (
                <p style={{ color: 'orange', textAlign: 'center', padding: '40px' }}>Loading wallet token balances...</p>
            ) : filteredBlends.length === 0 ? (
                <p style={{ textAlign: 'center', color: '#888', padding: '40px' }}>No active recipes found for this filter.</p>
            ) : (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '20px', marginTop: '20px' }}>
                    {filteredBlends.map(blend => {
                        const nftIngredients = blend.ingredients.filter(ing => ing.type === 'NFT');
                        const tokenIngredients = blend.ingredients.filter(ing => ing.type === 'TOKEN');

                        const canAffordNfts = nftIngredients.every(
                            ing => (userNftCountsByTemplate.get(Number(ing.templateId)) || 0) >= ing.count
                        );

                        const canAffordTokens = tokenIngredients.every(ing => {
                            const balanceString = tokenBalances[ing.symbol];
                            const balanceNum = parseFloat((balanceString || "").split(" ")[0]) || 0;
                            return balanceNum >= ing.amount;
                        });

                        const canAfford = canAffordNfts && canAffordTokens;
                        let buttonText = 'Select Items';
                        if (!canAfford) {
                            if (!canAffordNfts && !canAffordTokens) buttonText = 'Missing NFTs & Tokens';
                            else if (!canAffordNfts) buttonText = 'Missing Required NFTs';
                            else buttonText = 'Insufficient Tokens';
                        }

                        const rawResultImg = blend.resultTemplateData?.immutable_data?.img;
                        const resultImg = resolveIpfsUrl(rawResultImg) || placeholderImage;

                        return (
                            <div key={blend.id} style={{ border: '1px solid #444', borderRadius: '10px', padding: '16px', background: 'rgba(255,255,255,0.03)', display: 'flex', flexDirection: 'column' }}>
                                <h3 style={{ textAlign: 'center', marginTop: 0, minHeight: '44px', color: '#00e5ff' }}>
                                    {blend.blendName || `Recipe #${blend.id}`}
                                </h3>

                                <div>
                                    <h4 style={{ margin: '10px 0 6px 0', fontSize: '13px', color: '#aaa' }}>Ingredients Required:</h4>
                                    {blend.ingredients.map((ing, index) => {
                                        if (ing.type === 'NFT') {
                                            const userCount = userNftCountsByTemplate.get(Number(ing.templateId)) || 0;
                                            const hasEnough = userCount >= ing.count;
                                            const rawImg = ing.templateData?.immutable_data?.img;
                                            const imgSrc = resolveIpfsUrl(rawImg) || placeholderImage;

                                            return (
                                                <div key={`${index}-${ing.templateId}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                                    <img src={imgSrc} alt="NFT" style={{ width: '36px', height: '36px', objectFit: 'contain', background: '#222', borderRadius: '4px' }} />
                                                    <span style={{ flexGrow: 1, fontSize: '13px' }}>{ing.count}x {ing.templateData?.name || `Template #${ing.templateId}`}</span>
                                                    <span style={{ marginLeft: 'auto', color: hasEnough ? '#4caf50' : '#ff4b2b', fontSize: '12px', fontWeight: 'bold' }}>
                                                        ({userCount}/{ing.count})
                                                    </span>
                                                </div>
                                            );
                                        } else {
                                            const balanceString = tokenBalances[ing.symbol];
                                            const balanceNum = parseFloat((balanceString || "").split(" ")[0]) || 0;
                                            const hasEnough = balanceNum >= ing.amount;

                                            return (
                                                <div key={`${index}-${ing.symbol}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                                                    <span style={{ width: '36px', textAlign: 'center', fontWeight: 'bold', fontSize: '11px', color: '#ff9800' }}>🪙</span>
                                                    <span style={{ flexGrow: 1, fontSize: '13px' }}>{ing.amount.toLocaleString()} {ing.symbol}</span>
                                                    <span style={{ marginLeft: 'auto', color: hasEnough ? '#4caf50' : '#ff4b2b', fontSize: '12px', fontWeight: 'bold' }}>
                                                        ({balanceNum.toLocaleString()}/{ing.amount.toLocaleString()})
                                                    </span>
                                                </div>
                                            );
                                        }
                                    })}
                                </div>

                                <div style={{ margin: '12px 0', textAlign: 'center', fontSize: '20px', color: '#ff9800' }}>⬇️</div>

                                <div>
                                    <h4 style={{ margin: '0 0 6px 0', fontSize: '13px', color: '#aaa' }}>Craft Outcome:</h4>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <img src={resultImg} alt="Outcome" style={{ width: '48px', height: '48px', objectFit: 'contain', background: '#222', borderRadius: '6px' }} />
                                        <span style={{ fontWeight: 'bold', fontSize: '14px', color: '#fff' }}>1x {blend.resultTemplateData?.name || `Template #${blend.resultTemplateId}`}</span>
                                    </div>
                                </div>

                                <button
                                    onClick={() => { setSelectedBlend(blend); setSelectedAssets({}); setFeedback(''); }}
                                    disabled={!canAfford || isBlending || playerData?.isBlacklisted}
                                    className="modern-button"
                                    style={{
                                        width: '100%',
                                        marginTop: '15px',
                                        padding: '12px',
                                        background: canAfford ? 'linear-gradient(145deg, #28a745, #218838)' : '#333',
                                        cursor: canAfford ? 'pointer' : 'not-allowed',
                                        color: canAfford ? '#fff' : '#888'
                                    }}
                                >
                                    {buttonText}
                                </button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

export default BlendingPage;